// client/src/components/plataforma/franquias/FranquiaUsersPage.js - VERSÃO FINAL 100% CORRIGIDA
import React, { useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Plus, RefreshCw, Search, Filter as FilterIcon, Eye, Key, Trash2 } from 'lucide-react';
import EquipePanel from '../EquipePanel';
import useAlert from '../../../hooks/useAlert';
import PermissionGuard from '../../routing/PermissionGuard';
import { useUserFranquiasByFranquia, useRelationshipsMutations } from '../../../hooks/useRelationships';
import { useMutateUsers } from '../../../hooks/useUsers';
import ActionsDrawer from '../../common/ActionsDrawer';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';

const FranquiaUsersPage = () => {
    const { franquiaId } = useParams();
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const [searchInput, setSearchInput] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Hooks de Dados e Mutations
    const { data: response, isLoading, refetch } = useUserFranquiasByFranquia(franquiaId, { enabled: !!franquiaId });
    const { resetUserPassword } = useMutateUsers();
    const { deleteUserFranquia } = useRelationshipsMutations();

    const processedUsers = useMemo(() => {
        const rawData = response?.data || (Array.isArray(response) ? response : []);
        return rawData.map(vinculo => {
            const user = vinculo.user || {};
            const role = vinculo.role || {};
            return {
                ...user,
                _id: user._id || user.id,
                relationshipId: vinculo._id,
                name: user.name || 'Usuário sem nome',
                email: user.email || 'N/A',
                telefone: user.telefone || '-',
                roleName: role.name || 'Admin Franquia',
                isActive: vinculo.status === 'ativo',
                photoUrl: user.photoUrl || user.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random`
            };
        }).filter(u => {
            const term = searchInput.toLowerCase();
            return !term || u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term);
        });
    }, [response, searchInput]);

    const handleEdit = useCallback((u) => {
        navigate(`/franquias/${franquiaId}/admins/${u._id}/editar`, { state: { franquiaId, userId: u._id, isCreatingMode: false }});
        setSelectedUser(null);
    }, [navigate, franquiaId]);

    // ✅ CORREÇÃO: Reset de senha utilizando a mutation do usersController
    const handleResetPassword = useCallback((userId, userName) => {
        if (window.confirm(`Confirmar reset de senha para ${userName}?`)) {
            resetUserPassword.mutate(userId, {
                onSuccess: (data) => {
                    const tempPass = data.temporaryPassword || data.data?.temporaryPassword;
                    setAlert('Senha resetada com sucesso!', 'success');
                    if (tempPass) {
                        setTimeout(() => {
                            setAlert(`CÓDIGO TEMPORÁRIO PARA ${userName.toUpperCase()}: ${tempPass}`, 'info', 25000);
                        }, 500);
                    }
                },
                onError: (err) => setAlert(err.response?.data?.error || 'Erro ao resetar senha', 'danger')
            });
        }
        setSelectedUser(null);
    }, [resetUserPassword, setAlert]);

    // ✅ CORREÇÃO: Remoção de administrador da franquia
    const handleDelete = useCallback((user) => {
        if (window.confirm(`Remover acesso de ${user.name} desta franquia?`)) {
            deleteUserFranquia.mutate(user.relationshipId, {
                onSuccess: () => {
                    setAlert('Administrador removido com sucesso!', 'success');
                    refetch();
                },
                onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover', 'danger')
            });
        }
        setSelectedUser(null);
    }, [deleteUserFranquia, setAlert, refetch]);

    return (
        <PermissionGuard requiredPermission="franquias:manage_admins">
            <div className="page-management-container">
                {/* Antes montava o cabecalho na mao — os icones dos 3 botoes
                    tambem nao tinham className="icon" (o gap so vem dessa
                    classe), entao ficavam colados no texto. Convertido pra
                    PageHeader (navBack + actions). */}
                <PageHeader
                    icon={<Shield />}
                    title={`Admins da Franquia (${processedUsers.length})`}
                    navBack={{ to: '/franquias', text: 'Voltar' }}
                    actions={<>
                        <button onClick={() => setShowFilters(!showFilters)} className="action-button action-button-secondary">
                            <FilterIcon size={18} className="icon" /> Filtros
                        </button>
                        <button onClick={() => refetch()} className="action-icon-button" title="Atualizar">
                            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                        </button>
                    </>}
                />

                {showFilters && (
                    <div className="content-panel mb-4 p-4">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Buscar por nome ou e-mail..." className="form-input pl-10" />
                        </div>
                    </div>
                )}

                <EquipePanel
                    users={processedUsers}
                    isLoading={isLoading}
                    onOpenActions={setSelectedUser}
                    emptyStateMessage="Nenhum administrador encontrado para esta franquia."
                />
            </div>

            <ActionsDrawer
                open={!!selectedUser}
                onClose={() => setSelectedUser(null)}
                title={selectedUser?.name || 'Usuário'}
                subtitle={selectedUser?.roleName}
                actions={selectedUser ? [
                    {
                        key: 'editar',
                        label: 'Visualizar / Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEdit(selectedUser),
                    },
                    {
                        key: 'resetar-senha',
                        label: 'Resetar Senha',
                        icon: <Key size={20} className="icon" />,
                        onClick: () => handleResetPassword(selectedUser._id || selectedUser.id, selectedUser.name),
                    },
                    {
                        key: 'excluir',
                        label: 'Remover da Franquia',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedUser),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={() => navigate(`/franquias/${franquiaId}/admins/novo`, { state: { franquiaId, isCreatingMode: true, allowedRoles: ['franquia_admin'], defaultRole: 'franquia_admin' }})} title="Novo Administrador" icon={<Shield size={26} className="icon" />} />
        </PermissionGuard>
    );
};

export default FranquiaUsersPage;